import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, Edit2, FileText, Undo2 } from 'lucide-react';
import QRCode from 'qrcode';
import { buildPublicQrUrl } from '../../utils/publicOrigin';
import { lettersApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { LetterDocument } from './LetterDocument';
import { NewLetterEditor } from './new-editor/NewLetterEditor';
import type { PdfDownloaderFn } from './new-editor/components/PreviewPane';
import {
  DraftSignaturePlacement,
  formatSignatureBoxLabel,
  LETTER_PAGE_HEIGHT_PX,
  LETTER_PAGE_WIDTH_PX,
  sanitizeDraftSignaturePlacement,
} from './signatureLayoutShared';

interface LetterDetail {
  id: number;
  serial_number: number;
  letter_number: string;
  subject: string;
  letter_date: string;
  file_name?: string;
  department?: string;
  tags?: string[];
  assigned_signatory_user_id?: number | null;
  assigned_signatory_name?: string;
  approved_signature_html?: string | null;
  qr_code_data?: string | null;
  qr_code_token?: string | null;
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected';
  created_by?: number | string | null;
  body_html: string;
  letterhead_html: string;
  footer_html: string;
  updated_at?: string;
}

interface LetterTemplateDetail {
  id: number;
  template_name: string;
  body_html: string;
  letterhead_html?: string;
  footer_html?: string;
  tags?: string[];
  updated_at?: string;
}

interface SignatoryOption {
  id: number;
  name: string;
  email: string;
  signer_name?: string;
  designation?: string;
  has_signature?: number | boolean;
}

interface SignaturePlacementConfig {
  markAllPages: boolean;
  base: DraftSignaturePlacement;
  overrides: Record<string, DraftSignaturePlacement>;
  skippedPages?: number[];
}

const emptyParagraph = '<p></p>';
const departments = ['General', 'Finance', 'Sales', 'Operations', 'Customer Excellence'];

// Must match IMPORTED_DOCX_STORAGE_KEY in CreateLetterSelection.tsx. A Word (.docx)
// upload is converted to HTML there, stashed in sessionStorage, then consumed here
// so it loads into the editor body exactly like a typed letter.
const IMPORTED_DOCX_STORAGE_KEY = 'sepl-import-docx';

const consumeImportedDocx = (importedFlag: string | null): { html: string; fileName: string } | null => {
  if (importedFlag !== 'docx') return null;
  try {
    const raw = sessionStorage.getItem(IMPORTED_DOCX_STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(IMPORTED_DOCX_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    const html = typeof parsed?.html === 'string' ? parsed.html.trim() : '';
    if (!html) return null;
    return { html, fileName: typeof parsed?.fileName === 'string' ? parsed.fileName : '' };
  } catch {
    return null;
  }
};
const SIGNATURE_MARKER_CLASS = 'sepl-draft-signature-meta';
const SIGNATURE_MARKER_PREFIX = 'sepl-signature-meta-';
const SIGNATURE_CONFIG_PREFIX = 'sepl-signature-config-';
const defaultDraftSignaturePlacement: DraftSignaturePlacement = {
  pageIndex: 0,
  x: 56,
  y: 850,
  width: 168,
  height: 140,
};

const sanitizePlacement = (input?: Partial<DraftSignaturePlacement> | null): DraftSignaturePlacement => (
  sanitizeDraftSignaturePlacement(input, defaultDraftSignaturePlacement)
);

const defaultSignaturePlacementConfig: SignaturePlacementConfig = {
  markAllPages: false,
  base: defaultDraftSignaturePlacement,
  overrides: {},
  skippedPages: [],
};

const buildSignatureMarkerClass = (placement: DraftSignaturePlacement) => {
  const pageIndex = Math.max(0, Math.round(Number(placement.pageIndex) || 0));
  const x = Math.max(0, Math.round(Number(placement.x) || 0));
  const y = Math.max(0, Math.round(Number(placement.y) || 0));
  const width = Math.max(80, Math.round(Number(placement.width) || 168));
  const height = Math.max(64, Math.round(Number(placement.height) || 140));
  return `${SIGNATURE_MARKER_PREFIX}p${pageIndex}-x${x}-y${y}-w${width}-h${height}`;
};

const parseSignatureMarkerClass = (className: string): DraftSignaturePlacement | null => {
  const match = className.match(/sepl-signature-meta-p(\d+)-x(\d+)-y(\d+)-w(\d+)-h(\d+)/i);
  if (!match) return null;
  return {
    pageIndex: Number(match[1]) || 0,
    x: Number(match[2]) || defaultDraftSignaturePlacement.x,
    y: Number(match[3]) || defaultDraftSignaturePlacement.y,
    width: Number(match[4]) || defaultDraftSignaturePlacement.width,
    height: Number(match[5]) || defaultDraftSignaturePlacement.height,
  };
};

const compactSignatureConfig = (config: SignaturePlacementConfig) => {
  const normalizedOverrides: Record<string, DraftSignaturePlacement> = {};
  Object.entries(config.overrides || {}).forEach(([key, value]) => {
    const page = String(Math.max(0, Number(key) || 0));
    normalizedOverrides[page] = sanitizePlacement(value);
  });
  return {
    markAllPages: !!config.markAllPages,
    base: sanitizePlacement(config.base),
    overrides: normalizedOverrides,
    skippedPages: (config.skippedPages || []).filter((n) => typeof n === 'number'),
  };
};

const encodeSignatureConfigToken = (config: SignaturePlacementConfig) => encodeURIComponent(JSON.stringify(compactSignatureConfig(config)));

const decodeSignatureConfigToken = (token: string): SignaturePlacementConfig | null => {
  try {
    const parsed = JSON.parse(decodeURIComponent(token));
    const base = sanitizePlacement(parsed?.base);
    const overrides: Record<string, DraftSignaturePlacement> = {};
    Object.entries(parsed?.overrides || {}).forEach(([key, value]) => {
      overrides[String(Math.max(0, Number(key) || 0))] = sanitizePlacement(value as Partial<DraftSignaturePlacement>);
    });
    const markAllPages = Boolean(parsed?.markAllPages);
    const rawSkippedPages = Array.isArray(parsed?.skippedPages)
      ? (parsed.skippedPages as number[]).filter((n: unknown) => typeof n === 'number')
      : [];
    return {
      markAllPages,
      base,
      overrides,
      // Honour skipped pages even when "Mark Sig on All Pages" is on: enabling
      // that toggle already clears stale skips at the source, so saved skips are
      // intentional per-page removals.
      skippedPages: rawSkippedPages,
    };
  } catch {
    return null;
  }
};

const extractSignaturePlacementFromBody = (html: string): { cleanHtml: string; config: SignaturePlacementConfig | null } => {
  if (!html) {
    return { cleanHtml: html, config: null };
  }

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const marker = doc.querySelector(`.${SIGNATURE_MARKER_CLASS}`);
    const classMarker = doc.querySelector('[class*="sepl-signature-meta-"]');
    const configMarker = doc.querySelector('[class*="sepl-signature-config-"]');
    let config: SignaturePlacementConfig | null = null;

    if (configMarker instanceof HTMLElement) {
      const match = configMarker.className.match(/sepl-signature-config-([^\s]+)/i);
      if (match?.[1]) {
        config = decodeSignatureConfigToken(match[1]);
      }
      configMarker.remove();
    }

    if (!config && marker?.textContent) {
      const parsed = JSON.parse(marker.textContent);
      config = {
        markAllPages: false,
        base: sanitizePlacement({
          pageIndex: Number(parsed?.pageIndex) || 0,
          x: Number(parsed?.x),
          y: Number(parsed?.y),
          width: Number(parsed?.width),
          height: Number(parsed?.height),
        }),
        overrides: {},
      };
      marker.remove();
    }

    if (!config && classMarker instanceof HTMLElement) {
      const placement = parseSignatureMarkerClass(classMarker.className);
      if (placement) {
        config = {
          markAllPages: false,
          base: sanitizePlacement(placement),
          overrides: {},
        };
      }
      classMarker.remove();
    }

    // Cleanup legacy visible JSON marker blocks from old implementation
    Array.from(doc.body.querySelectorAll('div, p, span')).forEach((el) => {
      const text = (el.textContent || '').trim();
      if (/^\{[\s\S]*"pageIndex"\s*:\s*\d+[\s\S]*"x"\s*:[\s\S]*"y"\s*:[\s\S]*"width"\s*:[\s\S]*"height"\s*:[\s\S]*\}$/.test(text)) {
        el.remove();
      }
    });

    return { cleanHtml: doc.body.innerHTML || emptyParagraph, config };
  } catch {
    return { cleanHtml: html, config: null };
  }
};

const injectSignaturePlacementIntoBody = (html: string, config: SignaturePlacementConfig) => {
  const base = html || emptyParagraph;
  const withoutExisting = base
    .replace(new RegExp(`<div[^>]*class=["'][^"']*${SIGNATURE_MARKER_CLASS}[^"']*["'][^>]*>[\\s\\S]*?<\\/div>`, 'gi'), '')
    .replace(new RegExp(`<span[^>]*class=["'][^"']*${SIGNATURE_MARKER_PREFIX}[^"']*["'][^>]*><\\/span>`, 'gi'), '')
    .replace(new RegExp(`<span[^>]*class=["'][^"']*${SIGNATURE_CONFIG_PREFIX}[^"']*["'][^>]*><\\/span>`, 'gi'), '')
    .replace(/<p>\s*\{[\s\S]*"pageIndex"\s*:\s*\d+[\s\S]*"x"\s*:[\s\S]*"y"\s*:[\s\S]*"width"\s*:[\s\S]*"height"\s*:[\s\S]*\}\s*<\/p>/gi, '');

  const markerClass = `${SIGNATURE_CONFIG_PREFIX}${encodeSignatureConfigToken(config)}`;
  const marker = `<span class="${markerClass}"></span>`;
  return `${withoutExisting}${marker}`;
};

const normalizeLetterDate = (value: unknown, fallback: string) => {
  if (typeof value === 'string' && value.trim()) {
    return value.slice(0, 10);
  }
  return fallback;
};

const normalizeLetterTags = (value: unknown): string[] => {
  const source = Array.isArray(value) ? value : [];
  return Array.from(new Set(source.map((tag) => String(tag || '').trim()).filter(Boolean))).slice(0, 20);
};

const isSelfSignatoryOption = (option: SignatoryOption | null | undefined, user: { id?: string; email?: string } | null | undefined) => {
  if (!option || !user) return false;
  const byId = String(option.id) === String(user.id);
  const byEmail = String(option.email || '').trim().toLowerCase() === String(user.email || '').trim().toLowerCase();
  return byId || (!!user.email && byEmail);
};

interface CreateLetterPageProps {
  mode?: 'letter' | 'template';
}

export function CreateLetterPage({ mode = 'letter' }: CreateLetterPageProps) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const exportRenderRef = useRef<HTMLDivElement>(null);
  // Holds the editor's accurate PDF exporter (registered by PreviewPane). The
  // header "Download PDF" button uses this so the downloaded PDF matches the
  // live editor/preview exactly (tables, alignment, signature placement).
  const editorPdfDownloaderRef = useRef<PdfDownloaderFn | null>(null);
  const isTemplateMode = mode === 'template';
  const letterId = searchParams.get('letterId');
  const templateId = searchParams.get('templateId');
  const useTemplateId = searchParams.get('useTemplateId');
  const isEditingExistingLetter = isTemplateMode ? Boolean(templateId) : Boolean(letterId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaveEnabled] = useState(true);
  const [subject, setSubject] = useState('Untitled Letter');
  const [fileName, setFileName] = useState('');
  const [letterDate, setLetterDate] = useState(new Date().toISOString().slice(0, 10));
  const [bodyHtml, setBodyHtml] = useState(emptyParagraph);
  const [letterheadHtml, setLetterheadHtml] = useState('');
  const [footerHtml, setFooterHtml] = useState('');
  const [department, setDepartment] = useState('General');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [assignedSignatoryUserId, setAssignedSignatoryUserId] = useState('');
  const [signatoryOptions, setSignatoryOptions] = useState<SignatoryOption[]>([]);
  const [currentLetter, setCurrentLetter] = useState<LetterDetail | null>(null);
  const [currentTemplate, setCurrentTemplate] = useState<LetterTemplateDetail | null>(null);
  const [generatedQrCodeData, setGeneratedQrCodeData] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [signatureConfig, setSignatureConfig] = useState<SignaturePlacementConfig>(defaultSignaturePlacementConfig);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsRequiredFlow, setDetailsRequiredFlow] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const latestDraftSnapshotRef = useRef('');

  const canEdit = useMemo(() => {
    const permissions = user?.permissions;
    const hasPerm = permissions?.can_view_letters || permissions?.is_admin;
    // Rejected letters are intentionally editable so authors can correct and
    // resubmit them. Only pending/approved letters stay locked.
    const status = currentLetter?.status;
    const locked = status === 'pending_approval' || status === 'approved';
    return !!hasPerm && !locked;
  }, [user, currentLetter?.status]);

  const effectiveQrCodeData = currentLetter?.qr_code_data || generatedQrCodeData || null;

  // The creator may pull a letter that is still waiting for approval back to
  // draft, fix it, and resubmit — without the signatory having to reject it.
  const canRecall = useMemo(
    () =>
      !isTemplateMode &&
      currentLetter?.status === 'pending_approval' &&
      !!user?.id &&
      String(currentLetter?.created_by ?? '') === String(user.id),
    [currentLetter?.created_by, currentLetter?.status, isTemplateMode, user?.id],
  );
  const [recalling, setRecalling] = useState(false);
  const handleRecall = useCallback(async () => {
    if (!currentLetter?.id || !canRecall) return;
    const ok = window.confirm(
      'Recall this letter from approval?\n\nIt will be removed from the signatory\'s approval queue and become an editable draft. After making changes, click "Submit for Signature" again.',
    );
    if (!ok) return;
    setRecalling(true);
    setSaveError('');
    try {
      const response = await lettersApi.recall(currentLetter.id);
      setCurrentLetter(response.data);
      setSaveMessage('✓ Letter recalled — you can edit it now');
      window.setTimeout(() => setSaveMessage(''), 3500);
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || 'Could not recall the letter. Please try again.');
    } finally {
      setRecalling(false);
    }
  }, [canRecall, currentLetter?.id]);

  const hasRequiredDetails = useMemo(() => {
    if (isTemplateMode) {
      return !!String(fileName || '').trim();
    }
    return (
      !!String(fileName || '').trim()
      && !!String(letterDate || '').trim()
      && !!String(department || '').trim()
    );
  }, [department, fileName, isTemplateMode, letterDate]);

  const buildDraftSnapshot = useCallback(() => {
    return JSON.stringify({
      subject,
      fileName,
      letterDate,
      department,
      tags,
      assignedSignatoryUserId: assignedSignatoryUserId || null,
      bodyHtml: injectSignaturePlacementIntoBody(bodyHtml, signatureConfig),
      status: 'draft',
    });
  }, [assignedSignatoryUserId, bodyHtml, department, fileName, letterDate, signatureConfig, subject, tags]);

  useEffect(() => {
    latestDraftSnapshotRef.current = buildDraftSnapshot();
  }, [buildDraftSnapshot]);

  const resetToBlankEditor = useCallback((template?: { letterheadHtml?: string; footerHtml?: string }) => {
    const today = new Date().toISOString().slice(0, 10);
    setCurrentLetter(null);
    setCurrentTemplate(null);
    setSubject('Untitled Letter');
    setFileName('');
    setLetterDate(today);
    setDepartment('General');
    setTags([]);
    setAssignedSignatoryUserId('');
    setBodyHtml(emptyParagraph);
    setSignatureConfig(defaultSignaturePlacementConfig);
    setLetterheadHtml(template?.letterheadHtml || '');
    setFooterHtml(template?.footerHtml || '');
  }, []);

  // Read the latest user without making the loader depend on the object's
  // identity: a session refresh must never reload the letter being edited.
  const userRef = useRef(user);
  userRef.current = user;

  const loadLetterEditor = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError('');
      setAccessDenied(false);
      const [templateRes, signatoryRes] = await Promise.all([
        lettersApi.getTemplate(),
        isTemplateMode ? Promise.resolve({ data: [] }) : lettersApi.listSignatoryOptions(),
      ]);
      setLetterheadHtml(templateRes.data.letterheadHtml || '');
      setFooterHtml(templateRes.data.footerHtml || '');
      // Guard against a non-array payload (e.g. an error object from a failed
      // or 401-ing request) — `signatoryOptions.find` is called during render,
      // so a non-array here crashed the whole editor (ErrorBoundary).
      setSignatoryOptions(Array.isArray(signatoryRes.data) ? signatoryRes.data : []);

      if (isTemplateMode && templateId) {
        const templateResById = await lettersApi.getTemplateById(templateId);
        const template = templateResById?.data || {};
        setCurrentTemplate(template);
        setSubject(template.template_name || 'Untitled Template');
        setFileName(template.template_name || '');
        setTags(normalizeLetterTags(template.tags));
        const parsedBody = extractSignaturePlacementFromBody(template.body_html || emptyParagraph);
        setBodyHtml(parsedBody.cleanHtml || emptyParagraph);
        setSignatureConfig(defaultSignaturePlacementConfig);
        setLetterheadHtml(template.letterhead_html || templateRes.data.letterheadHtml || '');
        setFooterHtml(template.footer_html || templateRes.data.footerHtml || '');
        setShowDetailsModal(false);
        setDetailsRequiredFlow(false);
      } else if (!isTemplateMode && letterId) {
        try {
          const letterRes = await lettersApi.get(letterId);
          const letter = letterRes?.data || {};
          setCurrentLetter(letter);
          setSubject(letter.subject || 'Untitled Letter');
          setFileName(letter.file_name || '');
          setLetterDate(normalizeLetterDate(letter.letter_date, new Date().toISOString().slice(0, 10)));
          setDepartment(letter.department || 'General');
          setTags(normalizeLetterTags(letter.tags));
          setAssignedSignatoryUserId(letter.assigned_signatory_user_id ? String(letter.assigned_signatory_user_id) : '');
          const parsedBody = extractSignaturePlacementFromBody(letter.body_html || emptyParagraph);
          setBodyHtml(parsedBody.cleanHtml || emptyParagraph);
          setSignatureConfig(parsedBody.config || defaultSignaturePlacementConfig);
          setLetterheadHtml(letter.letterhead_html || templateRes.data.letterheadHtml || '');
          setFooterHtml(letter.footer_html || templateRes.data.footerHtml || '');
          setShowDetailsModal(false);
          setDetailsRequiredFlow(false);
        } catch (err: any) {
          if (err?.response?.status === 404) {
            resetToBlankEditor({
              letterheadHtml: templateRes.data.letterheadHtml,
              footerHtml: templateRes.data.footerHtml,
            });
            const params = new URLSearchParams(searchParams);
            params.set('page', 'letters-create');
            params.delete('letterId');
            setSearchParams(params);
            return;
          }
          throw err;
        }
      } else if (!isTemplateMode && useTemplateId) {
        const selectedTemplateRes = await lettersApi.getTemplateById(useTemplateId);
        const selectedTemplate = selectedTemplateRes?.data || {};
        resetToBlankEditor({
          letterheadHtml: selectedTemplate.letterhead_html || templateRes.data.letterheadHtml,
          footerHtml: selectedTemplate.footer_html || templateRes.data.footerHtml,
        });
        setFileName(selectedTemplate.template_name || '');
        setSubject(selectedTemplate.template_name || 'Untitled Letter');
        setTags(normalizeLetterTags(selectedTemplate.tags));
        const parsedBody = extractSignaturePlacementFromBody(selectedTemplate.body_html || emptyParagraph);
        setBodyHtml(parsedBody.cleanHtml || emptyParagraph);
        setSignatureConfig(defaultSignaturePlacementConfig);

        const selfOption = (signatoryRes.data || []).find((option: SignatoryOption) => (
          isSelfSignatoryOption(option, userRef.current)
          && (option.has_signature === true || option.has_signature === 1)
        ));
        if (selfOption) {
          setAssignedSignatoryUserId(String(selfOption.id));
        }
        setShowDetailsModal(true);
        setDetailsRequiredFlow(true);
      } else {
        resetToBlankEditor({
          letterheadHtml: templateRes.data.letterheadHtml,
          footerHtml: templateRes.data.footerHtml,
        });

        // If we arrived from a Word (.docx) upload, load the converted HTML into
        // the editor body and prefill the file name/subject so the user only has
        // to confirm details, position the signature box, and send for approval.
        const importedDocx = consumeImportedDocx(searchParams.get('imported'));
        if (importedDocx) {
          setBodyHtml(importedDocx.html);
          if (importedDocx.fileName) {
            setFileName(importedDocx.fileName);
            setSubject(importedDocx.fileName);
          }
        }

        if (!isTemplateMode) {
          const selfOption = (signatoryRes.data || []).find((option: SignatoryOption) => (
            isSelfSignatoryOption(option, userRef.current)
            && (option.has_signature === true || option.has_signature === 1)
          ));
          if (selfOption) {
            setAssignedSignatoryUserId(String(selfOption.id));
          }
        }

        setShowDetailsModal(true);
        setDetailsRequiredFlow(true);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load letter.';
      setAccessDenied(err?.response?.status === 403);
      setLoadError(msg);
      resetToBlankEditor();
    } finally {
      setLoading(false);
      setIsInitialized(true);
    }
  }, [isTemplateMode, letterId, resetToBlankEditor, searchParams, setSearchParams, templateId, useTemplateId, user?.id]);

  useEffect(() => {
    loadLetterEditor();
  }, [loadLetterEditor]);

  useEffect(() => {
    if (currentLetter?.qr_code_data) {
      setGeneratedQrCodeData(null);
      return;
    }

    const token = currentLetter?.qr_code_token;
    if (!token) {
      setGeneratedQrCodeData(null);
      return;
    }

    let cancelled = false;
    const qrUrl = buildPublicQrUrl(token);
    QRCode.toDataURL(qrUrl, {
      errorCorrectionLevel: 'M',
      type: 'image/png',
      quality: 0.95,
      margin: 0,
      width: 260,
      color: {
        dark: '#000000',
        light: '#FFFFFF',
      },
    })
      .then((dataUrl) => {
        if (!cancelled) setGeneratedQrCodeData(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setGeneratedQrCodeData(null);
      });

    return () => {
      cancelled = true;
    };
  }, [currentLetter?.qr_code_data, currentLetter?.qr_code_token]);

  const updateParams = useCallback((page: string, extra: Record<string, string> = {}) => {
    const params = new URLSearchParams();
    params.set('page', page);
    Object.entries(extra).forEach(([key, value]) => params.set(key, value));
    setSearchParams(params);
  }, [setSearchParams]);

  const handleOpenDetailsEditor = () => {
    setDetailsError('');
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    if (detailsRequiredFlow && !hasRequiredDetails) {
      updateParams(isTemplateMode ? 'letters-view-templates' : 'letters-create');
      return;
    }
    setDetailsError('');
    setShowDetailsModal(false);
  };

  const handleConfirmDetails = async () => {
    if (!hasRequiredDetails) {
      setDetailsError(isTemplateMode ? 'Template Name is required.' : 'File Name, Date and Department are required.');
      return;
    }
    const trimmedFileName = String(fileName || '').trim();
    const trimmedSubject = String(subject || '').trim();

    // Check for duplicate name before closing the modal
    try {
      const checkParams = isTemplateMode
        ? { templateName: trimmedFileName, ...(currentTemplate?.id ? { excludeId: currentTemplate.id } : {}) }
        : { fileName: trimmedFileName, ...(currentLetter?.id ? { excludeId: currentLetter.id } : {}) };
      const checkRes = await lettersApi.checkName(checkParams);
      if (checkRes.data?.exists) {
        setDetailsError(
          isTemplateMode
            ? `A template named "${trimmedFileName}" already exists. Please use a different name.`
            : `A letter named "${trimmedFileName}" already exists. Please use a different file name.`
        );
        return;
      }
    } catch {
      // If the check fails, allow to proceed (backend will catch it on save)
    }

    if (trimmedFileName && (!trimmedSubject || trimmedSubject === 'Untitled Letter')) {
      setSubject(trimmedFileName);
    }
    setDetailsError('');
    setShowDetailsModal(false);
    setDetailsRequiredFlow(false);
    setDirty(true);
  };

  const handleSave = useCallback(async (status: 'draft' | 'pending_approval', isAutoSave = false) => {
    const autosaveSnapshotBeforeRequest = isAutoSave ? latestDraftSnapshotRef.current : '';
    try {
      if (!canEdit) return;
      if (!isAutoSave) {
        setSaving(true);
        setSaveError('');
      }

      if (isTemplateMode) {
        if (!String(fileName || '').trim()) {
          if (!isAutoSave) {
            setSaveError('Template name is required.');
            setSaving(false);
            setShowDetailsModal(true);
            setDetailsRequiredFlow(true);
          }
          return;
        }

        const templatePayload = {
          templateName: String(fileName || '').trim(),
          bodyHtml,
          tags,
          letterheadHtml,
          footerHtml,
        };

        const response = currentTemplate?.id
          ? await lettersApi.updateTemplate(currentTemplate.id, templatePayload)
          : await lettersApi.createTemplate(templatePayload);

        const nextTemplate = response.data;
        if (!isAutoSave) {
          setCurrentTemplate(nextTemplate);
          setSubject(nextTemplate.template_name || subject);
          setFileName(nextTemplate.template_name || fileName);
          // Do NOT re-feed bodyHtml from the save response (see the note in the
          // letter save path) — it forces an editor setContent re-parse that
          // reflows spacing. The editor already holds the authoritative content.
          setTags(normalizeLetterTags(nextTemplate.tags));
          setLetterheadHtml(nextTemplate.letterhead_html || letterheadHtml);
          setFooterHtml(nextTemplate.footer_html || footerHtml);
        }
        setDirty(false);

        if (!isAutoSave) {
          setSaveMessage('✓ Template saved');
          window.setTimeout(() => setSaveMessage(''), 2500);
        }

        if (!templateId || String(templateId) !== String(nextTemplate.id)) {
          updateParams('letters-create-template', { templateId: String(nextTemplate.id) });
        }

        setSaving(false);
        return;
      }

      if (status === 'pending_approval' && !assignedSignatoryUserId) {
        if (!isAutoSave) {
          setSaveError('Please select an authorized signatory.');
          setSaving(false);
        }
        return;
      }

      if (detailsRequiredFlow && !hasRequiredDetails) {
        if (!isAutoSave) {
          setSaveError('Please complete mandatory basic details before saving.');
          setSaving(false);
          setShowDetailsModal(true);
          setDetailsRequiredFlow(true);
        }
        return;
      }

      const payload = {
        subject,
        fileName,
        letterDate,
        department,
        tags,
        assignedSignatoryUserId: assignedSignatoryUserId || null,
        bodyHtml: injectSignaturePlacementIntoBody(bodyHtml, signatureConfig),
        status,
        isAutoSave,
      };

      const response = currentLetter?.id
        ? await lettersApi.update(currentLetter.id, payload)
        : await lettersApi.create(payload);

      const nextLetter = response.data;
      const parsedBody = extractSignaturePlacementFromBody(nextLetter.body_html || bodyHtml);
      if (!isAutoSave) {
        setCurrentLetter(nextLetter);
        setSubject(nextLetter.subject || subject);
        setFileName(nextLetter.file_name || fileName);
        setDepartment(nextLetter.department || department);
        setTags(normalizeLetterTags(nextLetter.tags));
        setAssignedSignatoryUserId(nextLetter.assigned_signatory_user_id ? String(nextLetter.assigned_signatory_user_id) : '');
      } else if (!currentLetter?.id && nextLetter?.id) {
        setCurrentLetter(nextLetter);
      }
      if (!isAutoSave) {
        // Do NOT re-feed bodyHtml from the save response. The editor already
        // holds the authoritative content (kept in sync via onChange), and the
        // backend returns a re-serialized/sanitized copy that differs as a
        // STRING — pushing it back triggers the editor's setContent re-parse,
        // which reflows paragraph spacing on "Submit for Signature".
        setSignatureConfig(parsedBody.config || signatureConfig);
        setLetterDate(normalizeLetterDate(nextLetter.letter_date, letterDate));
        setLetterheadHtml(nextLetter.letterhead_html || letterheadHtml);
        setFooterHtml(nextLetter.footer_html || footerHtml);
      }
      if (isAutoSave) {
        const hasNewerUnsavedChanges = latestDraftSnapshotRef.current !== autosaveSnapshotBeforeRequest;
        setDirty(hasNewerUnsavedChanges);
      } else {
        setDirty(false);
      }
      if (!isAutoSave) {
        setSaveMessage(status === 'pending_approval' ? '✓ Letter submitted for signature' : '✓ Draft saved');
        window.setTimeout(() => setSaveMessage(''), 2500);
      }
      if (isEditingExistingLetter && (!letterId || String(letterId) !== String(nextLetter.id))) {
        updateParams('letters-create', { letterId: String(nextLetter.id) });
      }
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error;
      const httpStatus = err?.response?.status;
      let msg = 'Save failed. Please try again.';
      if (httpStatus === 403) msg = 'You do not have permission to save letters.';
      else if (httpStatus === 401) msg = 'Session expired. Please log in again.';
      else if (serverMsg) msg = serverMsg;
      else if (err?.message) msg = `Save failed: ${err.message}`;
      if (isAutoSave) {
        msg = serverMsg || 'Auto-save failed. Your latest changes are not saved yet.';
      }
      setSaveError(msg);
      window.setTimeout(() => setSaveError(''), 5000);
    } finally {
      if (!isAutoSave) {
        setSaving(false);
      }
    }
  }, [assignedSignatoryUserId, bodyHtml, canEdit, currentLetter?.id, currentTemplate?.id, department, detailsRequiredFlow, fileName, footerHtml, hasRequiredDetails, isEditingExistingLetter, isTemplateMode, letterDate, letterId, letterheadHtml, signatureConfig, subject, tags, templateId, updateParams]);

  useEffect(() => {
    if (isTemplateMode) return;
    if (!isInitialized || !dirty || !canEdit || !autoSaveEnabled) return;

    const hasMeaningfulContent = (subject || '').trim() || (bodyHtml || '').replace(/<[^>]+>/g, '').trim();
    if (!hasMeaningfulContent) return;

    const timer = window.setTimeout(() => {
      handleSave('draft', true);
    }, 1500);

    return () => window.clearTimeout(timer);
  }, [isTemplateMode, subject, letterDate, bodyHtml, dirty, isInitialized, canEdit, autoSaveEnabled, handleSave]);

  const selectedSignatory = useMemo(
    () =>
      Array.isArray(signatoryOptions)
        ? signatoryOptions.find((option) => String(option.id) === assignedSignatoryUserId) || null
        : null,
    [assignedSignatoryUserId, signatoryOptions]
  );

  const isSelfSelected = useMemo(
    () => Boolean(isSelfSignatoryOption(selectedSignatory, user)),
    [selectedSignatory, user]
  );

  const addTag = useCallback((rawTag: string) => {
    const nextTag = String(rawTag || '').trim();
    if (!nextTag) return;
    setTags((prev) => {
      if (prev.includes(nextTag) || prev.length >= 20) {
        return prev;
      }
      return [...prev, nextTag];
    });
    setTagInput('');
    setDirty(true);
  }, []);

  const removeTag = useCallback((tagToRemove: string) => {
    setTags((prev) => prev.filter((tag) => tag !== tagToRemove));
    setDirty(true);
  }, []);

  const handleSubmitOrSign = useCallback(async () => {
    if (detailsRequiredFlow && !hasRequiredDetails) {
      setSaveError('Please complete mandatory basic details before submitting.');
      setShowDetailsModal(true);
      setDetailsRequiredFlow(true);
      return;
    }

    if (!isSelfSelected) {
      await handleSave('pending_approval');
      return;
    }

    try {
      if (!canEdit) return;
      if (!assignedSignatoryUserId) {
        setSaveError('Please select an authorized signatory.');
        return;
      }

      setSaving(true);
      setSaveError('');

      const payload = {
        subject,
        fileName,
        letterDate,
        department,
        tags,
        assignedSignatoryUserId: assignedSignatoryUserId || null,
        bodyHtml: injectSignaturePlacementIntoBody(bodyHtml, signatureConfig),
        status: 'pending_approval' as const,
        isAutoSave: false,
      };

      const response = currentLetter?.id
        ? await lettersApi.update(currentLetter.id, payload)
        : await lettersApi.create(payload);

      const nextLetter = response.data;
      await lettersApi.approve(nextLetter.id);

      setSaveMessage('✓ Letter signed successfully');
      window.setTimeout(() => setSaveMessage(''), 2500);
      setDirty(false);
      updateParams('letters-view', { letterId: String(nextLetter.id) });
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Sign failed. Please try again.';
      setSaveError(msg);
      window.setTimeout(() => setSaveError(''), 5000);
    } finally {
      setSaving(false);
    }
  }, [assignedSignatoryUserId, bodyHtml, canEdit, currentLetter?.id, department, detailsRequiredFlow, fileName, handleSave, hasRequiredDetails, isSelfSelected, letterDate, signatureConfig, subject, tags, updateParams]);

  const downloadPdf = async () => {
    try {
      setSaveError('');
      // Prefer the editor-accurate export (same renderer as the live preview).
      // This guarantees the downloaded PDF matches what the user sees in the
      // editor — correct tables, alignment, and signature placement — instead
      // of the divergent LetterDocument re-render below.
      if (editorPdfDownloaderRef.current) {
        await editorPdfDownloaderRef.current();
        return;
      }
      const rawPdfName = isTemplateMode
        ? (String(fileName || '').trim() || 'letter-template')
        : (currentLetter?.letter_number || fileName || subject || (currentLetter?.id ? `letter-${currentLetter.id}` : 'letter-draft'));
      const pdfName = String(rawPdfName || 'letter').trim().replace(/[\\/:*?"<>|]+/g, '-');
      const sourceEl = exportRenderRef.current;
      if (!sourceEl) throw new Error('PDF renderer is not ready yet.');

      const pageElements = Array.from(sourceEl.querySelectorAll('.sepl-letter-page')) as HTMLElement[];
      if (!pageElements.length) throw new Error('No letter pages found for PDF export.');

      const [{ jsPDF }, html2canvasModule] = await Promise.all([
        import('jspdf'),
        import('html2canvas'),
      ]);
      const html2canvas = html2canvasModule.default;

      const MAX_PDF_SIZE_BYTES = 2 * 1024 * 1024;

      const buildPdfBlob = async (scale: number, imageQuality: number) => {
        const pdf = new jsPDF({
          unit: 'px',
          format: [794, 1122],
          orientation: 'portrait',
          compress: true,
        });

        for (let index = 0; index < pageElements.length; index += 1) {
          const pageEl = pageElements[index];
          const canvas = await html2canvas(pageEl, {
            scale,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            width: LETTER_PAGE_WIDTH_PX,
            height: LETTER_PAGE_HEIGHT_PX,
            windowWidth: LETTER_PAGE_WIDTH_PX,
            windowHeight: LETTER_PAGE_HEIGHT_PX,
            scrollX: 0,
            scrollY: 0,
          });

          const imageData = canvas.toDataURL('image/jpeg', imageQuality);
          if (index > 0) {
            pdf.addPage([794, 1122], 'portrait');
          }
          pdf.addImage(imageData, 'JPEG', 0, 0, 794, 1122, undefined, 'MEDIUM');
        }

        const blob = pdf.output('blob');
        return { pdf, blob };
      };

      const compressionAttempts = [
        { scale: 1.8, quality: 0.88 },
        { scale: 1.6, quality: 0.84 },
        { scale: 1.4, quality: 0.8 },
        { scale: 1.25, quality: 0.76 },
        { scale: 1.1, quality: 0.72 },
        { scale: 1, quality: 0.68 },
        { scale: 0.9, quality: 0.62 },
        { scale: 0.82, quality: 0.58 },
        { scale: 0.75, quality: 0.54 },
      ];

      let selectedBlob: Blob | null = null;

      for (const attempt of compressionAttempts) {
        const { blob } = await buildPdfBlob(attempt.scale, attempt.quality);
        selectedBlob = blob;
        if (blob.size <= MAX_PDF_SIZE_BYTES) {
          break;
        }
      }

      if (!selectedBlob) throw new Error('Generated PDF is empty.');

      const url = URL.createObjectURL(selectedBlob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${pdfName}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setSaveError(err?.message || 'PDF download failed. Please try again.');
      window.setTimeout(() => setSaveError(''), 5000);
    }
  };

  if (loading) {
    return <div className="p-8 text-gray-600">Loading letter editor...</div>;
  }

  if (loadError) {
    if (accessDenied) {
      return (
        <div className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-amber-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-amber-100 bg-amber-50">
              <h3 className="text-base font-semibold text-amber-900">Access denied</h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-700">{loadError}</p>
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 text-sm font-medium"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center max-w-lg mx-auto">
          <p className="text-red-700 font-semibold mb-2">Failed to load letter editor</p>
          <p className="text-red-600 text-sm mb-4">{loadError}</p>
          <button onClick={loadLetterEditor} className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Top header bar */}
      <div className="bg-white border-b border-gray-200 px-3 md:px-6 py-3 flex flex-wrap items-start gap-3">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <div className="flex flex-wrap items-end gap-3 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <div className="text-xs text-gray-500 uppercase tracking-wide">{isTemplateMode ? 'Template Name' : 'File Name'}</div>
            <div className="text-sm font-semibold text-gray-900 truncate max-w-[320px]">{fileName || (isTemplateMode ? 'Untitled Template' : 'Untitled Letter')}</div>
            {canEdit && (
              <button
                type="button"
                onClick={handleOpenDetailsEditor}
                className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-violet-200 text-violet-700 hover:bg-violet-50"
                title="Edit details"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            )}
          </div>
          {canEdit && !isTemplateMode && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 uppercase tracking-wide whitespace-nowrap">Signatory</label>
              <select
                value={assignedSignatoryUserId}
                onChange={(e) => { setAssignedSignatoryUserId(e.target.value); setDirty(true); }}
                className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm bg-white min-w-[160px]"
              >
                <option value="">Select signatory</option>
                {signatoryOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {isSelfSignatoryOption(option, user)
                      ? 'Self'
                      : `${option.signer_name || option.name}${option.designation ? ` · ${option.designation}` : ''}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!isTemplateMode && !canEdit && assignedSignatoryUserId && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Signatory</span>
              <span className="text-sm font-medium text-gray-800 bg-violet-50 border border-violet-200 rounded-lg px-2.5 py-1">
                {(() => {
                  const matched = Array.isArray(signatoryOptions)
                    ? signatoryOptions.find((o) => String(o.id) === String(assignedSignatoryUserId))
                    : undefined;
                  if (!matched) return '';
                  return isSelfSignatoryOption(matched, user)
                    ? 'Self'
                    : `${matched.signer_name || matched.name}`;
                })()}
              </span>
            </div>
          )}
          </div>
          <div className="flex items-center gap-2 flex-wrap pl-0 md:pl-0">
            <span className="text-sm text-gray-500 uppercase tracking-wide">Tags</span>
            {tags.map((tag) => (
              <span key={tag} className="inline-flex items-center gap-1.5 bg-violet-100 text-violet-800 text-sm font-medium rounded-full px-3 py-1">
                {tag}
                {canEdit && (
                  <button type="button" onClick={() => removeTag(tag)} className="hover:text-violet-600 ml-0.5 text-base leading-none">×</button>
                )}
              </span>
            ))}
            {canEdit && (
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
                    e.preventDefault();
                    const t = tagInput.trim().replace(/,$/, '');
                    if (t && !tags.includes(t)) setTags((prev) => [...prev, t]);
                    setTagInput('');
                  }
                }}
                placeholder="Add tag…"
                className="text-sm border border-gray-300 rounded-full px-3 py-1.5 w-32 focus:outline-none focus:ring-1 focus:ring-violet-300"
              />
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto flex-shrink-0">
          {!isTemplateMode && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-1.5">
              <input
                type="checkbox"
                checked={signatureConfig.markAllPages}
                disabled={!canEdit}
                onChange={(e) => {
                  if (!canEdit) return;
                  const checked = e.target.checked;
                  setSignatureConfig((prev) => ({
                    ...prev,
                    markAllPages: checked,
                    base: { ...prev.base, pageIndex: 0 },
                    // Enabling "all pages" must clear any pages previously removed
                    // via the per-page ✕ button; otherwise the signature stays
                    // missing on those pages even though "all pages" is checked.
                    skippedPages: checked ? [] : prev.skippedPages,
                  }));
                  setDirty(true);
                }}
              />
              <span className="text-xs whitespace-nowrap">Mark Sig on All Pages</span>
            </label>
          )}
          {saveMessage && (
            <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2.5 py-1.5 rounded-lg font-medium">{saveMessage}</span>
          )}
          {saveError && (
            <span className="text-xs text-red-700 bg-red-50 border border-red-200 px-2.5 py-1.5 rounded-lg font-medium">⚠ {saveError}</span>
          )}
          {!saveMessage && !saveError && (saving
            ? <span className="text-xs text-violet-600 bg-violet-50 border border-violet-200 px-2.5 py-1.5 rounded-lg font-medium animate-pulse">Saving…</span>
            : <span className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded-lg">Auto Saved {currentLetter?.id ? '✓' : ''}</span>
          )}
          {isTemplateMode ? (
            <button
              onClick={() => handleSave('draft')}
              disabled={!canEdit || saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium disabled:opacity-60"
            >
              <FileText className="w-4 h-4" /> Save Template
            </button>
          ) : canRecall ? (
            <button
              onClick={handleRecall}
              disabled={recalling}
              title="Pull this letter back from approval so you can edit it and submit again"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium disabled:opacity-60"
            >
              <Undo2 className="w-4 h-4" /> {recalling ? 'Recalling…' : 'Recall for Editing'}
            </button>
          ) : (
            <button
              onClick={handleSubmitOrSign}
              disabled={!canEdit || saving || !assignedSignatoryUserId}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium disabled:opacity-60"
            >
              <FileText className="w-4 h-4" /> {isSelfSelected ? 'Sign Letter' : 'Submit for Signature'}
            </button>
          )}
          <button
            onClick={downloadPdf}
            disabled={isTemplateMode ? !String(fileName || '').trim() : !String(fileName || subject || '').trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-violet-200 bg-violet-50 text-violet-700 text-sm font-medium disabled:opacity-60"
          >
            <Download className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {showDetailsModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{isTemplateMode ? 'Create Template' : 'Enter Basic Details'}</h3>
            </div>
            <div className={`p-5 grid grid-cols-1 ${isTemplateMode ? 'md:grid-cols-1' : 'md:grid-cols-2'} gap-4`}>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">{isTemplateMode ? 'Template Name *' : 'File Name *'}</label>
                <input
                  type="text"
                  value={fileName}
                  disabled={!canEdit}
                  onChange={(e) => { setFileName(e.target.value); setDirty(true); }}
                  placeholder={isTemplateMode ? 'e.g., Offer_Letter_Template' : 'e.g., Proposal_2026'}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              {!isTemplateMode && (
                <>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Date *</label>
                    <input
                      type="date"
                      value={letterDate}
                      disabled={!canEdit}
                      onChange={(e) => { setLetterDate(e.target.value); setDirty(true); }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">Department *</label>
                    <select
                      value={department}
                      disabled={!canEdit}
                      onChange={(e) => { setDepartment(e.target.value); setDirty(true); }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
                    >
                      {departments.map((item) => (
                        <option key={item} value={item}>{item}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
            {detailsError ? (
              <div className="px-5 pb-2 text-sm text-red-600">⚠ {detailsError}</div>
            ) : null}
            <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleCloseDetailsModal}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
              >
                {detailsRequiredFlow ? 'Back' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleConfirmDetails}
                className="px-4 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700 text-sm font-medium"
              >
                {isTemplateMode ? 'Start Designing' : 'Create Letter'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1800px] mx-auto w-full p-4 md:p-6">
        <div className="h-[calc(100vh-220px)] min-h-[680px] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <NewLetterEditor
            title={fileName || subject || (isTemplateMode ? 'Untitled Template' : 'Untitled Letter')}
            content={bodyHtml}
            qrCodeData={effectiveQrCodeData}
            letterNumber={currentLetter?.status === 'approved' ? currentLetter?.letter_number || null : null}
            letterDate={currentLetter?.status === 'approved' ? currentLetter?.letter_date || null : null}
            approvedSignatureHtml={currentLetter?.approved_signature_html || null}
            signaturePlacementConfig={signatureConfig}
            enableDraftSignatureDrag={!!canEdit && !currentLetter?.approved_signature_html}
            registerPdfDownloader={(fn) => { editorPdfDownloaderRef.current = fn; }}
            onRemoveSignaturePage={(pageIndex) => {
              setSignatureConfig((prev) => ({
                ...prev,
                skippedPages: [...new Set([...(prev.skippedPages || []), pageIndex])],
              }));
              setDirty(true);
            }}
            onDraftSignaturePlacementChange={(next) => {
              setSignatureConfig((prev) => {
                if (prev.markAllPages) {
                  if (next.pageIndex === 0) {
                    return {
                      ...prev,
                      base: { ...next, pageIndex: 0 },
                    };
                  }
                  return {
                    ...prev,
                    overrides: {
                      ...(prev.overrides || {}),
                      [String(next.pageIndex)]: next,
                    },
                  };
                }

                return {
                  ...prev,
                  base: next,
                };
              });
              setDirty(true);
            }}
            editable={!!canEdit}
            onChange={(html) => {
              const parsedBody = extractSignaturePlacementFromBody(html);
              setBodyHtml(parsedBody.cleanHtml || html);
              if (parsedBody.config) {
                setSignatureConfig(parsedBody.config);
              }
              setDirty(true);
            }}
          />
        </div>
      </div>

      <div
        style={{ position: 'fixed', left: '-9999px', top: 0, width: '794px', pointerEvents: 'none', opacity: 0, zIndex: -1 }}
        aria-hidden="true"
      >
        <div ref={exportRenderRef}>
          <LetterDocument
            subject={subject}
            letterDate={letterDate}
            serialNumber={currentLetter?.serial_number}
            letterNumber={currentLetter?.letter_number}
            bodyHtml={bodyHtml}
            letterheadHtml={letterheadHtml}
            footerHtml={footerHtml}
            approvedSignatureHtml={currentLetter?.approved_signature_html}
            qrCodeData={effectiveQrCodeData}
            signaturePlacementConfig={signatureConfig}
            exportMode
          />
        </div>
      </div>
    </div>
  );
}
